import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import * as _ from 'underscore';

import { Messages } from '../../../../codegen/Localize';
import { showActionableToast } from '../../../../common/react/components/ActionableMessage';
import { FavoriteRepresentation, FavoritesResource } from '../../../../favorites/resources/FavoritesResource';
import {
  getHomeAddFavoriteSucceededAction,
  getHomeRemoveFavoriteSucceededAction
} from '../../../../home/actions/HomeActions';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { L10n } from '../../../../ts/L10n/L10n';
import { ContentFiltersKeys } from '../../../../ts/models/ContentFilters';
import { OrderKeys } from '../../../../ts/models/OrderKeys';
import { IExplorableItemTypeInfo, IFavoritableItemInfo } from '../../../../ts/models/resourceInfoParts';
import { getFavoritesSpaceDefaultSortOrder } from '../../../../ts/models/sort/FavoritesSortOptions';
import { Favorites } from '../../../../content/resources/Favorites';
import { FavoriteOrdering } from '../../../../ts/ServerApi/requests/OrderFavoritesRequest';
import { AssetListItem } from '../../../../ts/ServerApi/types/AssetListItem';
import { contentTypeCanBeFavorite, FavoriteItemType, IFavorite } from '../../../../ts/ServerApi/types/IFavorite';
import { IOrderClause } from '../../../../ts/ServerApi/types/IOrderClause';
import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import {
  AddContentFavorite,
  CallReorderItem,
  RemoveContentFavorite,
} from '../../../actions/AppActionTypes';
import { CallReorderItemAction } from '../../../actions/CallReorderItem';
import { reorderItem, ReorderItemAction } from '../../../actions/ReorderItem';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { setIgnoreNextOrderUrlChange, setOrderAction } from '../../../actions/SetOrderAction';
import { decrementContentCount, incrementContentCount } from '../../../actions/spaces/ContentCountsActions';
import { removeFromContentSelection } from '../../../actions/spaces/ContentExplorerSelectionActions';
import {
  addContentFavorite as addContentFavoriteAction,
  AddContentFavoriteAction,
  contentFavoriteAdded,
  contentFavoriteRemoved,
  RemoveContentFavoriteAction
} from '../../../actions/spaces/FavoritesActions';
import { doesSpaceSupportStylishFiltering } from '../../../actions/spaces/filters/FilterHelpers';
import { resetContentBusyFetching } from '../../../actions/spaces/RequestSpaceContentsActions';
import { selectSortOrder } from '../../../actions/spaces/SelectSortOrderAction';
import { getAppStore } from '../../../AppStore';
import { selectAssetListItems } from '../../../selectors/AssetListSelectors';
import {
  selectSpecificMixedContentFilterClause
} from '../../../selectors/ContentFilterSelectors';
import { selectContentFromAppState } from '../../../selectors/ContentSelectors';
import { selectContentSort } from '../../../selectors/ContentSortSelectors';
import { SessionUserSelectors } from '../../../selectors/SessionInfoSelectors';
import { selectSpaceInfo } from '../../../selectors/SpaceInfoSelectors';
import { ConfirmDragDropOrderDialog } from '../../../spaces/Collections/ConfirmDragDropOrderDialog';
import { isFavoritesSpace, isHomeSpace, SpaceInfo } from '../../../state/SpaceInfo';
import { IDataSourceInfo } from '../../../../ts/models/IDataSourceInfo';

export function* rootFavoritesSaga(): SagaIterator {
  yield takeEvery(AddContentFavorite, handleAddContentFavorite);
  yield takeEvery(RemoveContentFavorite, handleRemoveContentFavorite);
  yield takeEvery(CallReorderItem, handleCallReorderItem);
}

export function* handleAddContentFavorite(action: AddContentFavoriteAction) {
  try {
    yield call(addFavorite, action.item);

    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    if(isHomeSpace(spaceInfo)) {
      yield put(getHomeAddFavoriteSucceededAction(action.item.id, getFavoriteContentType(action.item.contentType)));
    } else {
      yield put(contentFavoriteAdded(action.item));
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.favoritesError()));
  }
}

export function addFavorite(item: IFavoritableItemInfo): Promise<IResult> {
  if(FeatureFlags.crassFavorites && item.contentType === 'metric') {
    const favorite: FavoriteRepresentation = {
      contentId: item.id,
      contentType: item.contentType,
    };

    const userId = SessionUserSelectors.sessionUserId(getAppStore().getState());
    return FavoritesResource.add(favorite, userId);
  }
  else {
    const idForFavoriting = item.idForFavoriting ? item.idForFavoriting : item.id;
    return Promise.resolve(Favorites.add(idForFavoriting, item.contentType));
  }
}

export function* handleRemoveContentFavorite(action: RemoveContentFavoriteAction) {
  try {
    yield call(removeFavorite, action.item);

    // we want to remove the item from state if we're in favorites space
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const favoriteFilterApplied = yield select(selectSpecificMixedContentFilterClause, ContentFiltersKeys.favorite);
    const removeFavoriteFromExplorer = favoriteFilterApplied && doesSpaceSupportStylishFiltering(spaceInfo);

    if (isFavoritesSpace(spaceInfo) || removeFavoriteFromExplorer) {
      yield put(decrementContentCount(action.item.id, spaceInfo.spaceType, action.item.contentType));
      yield put(contentFavoriteRemoved(action.item));
      yield put(removeFromContentSelection(action.item));
      showUndoRemoveFavoriteToast(action.item, true, spaceInfo);
    } else if (isHomeSpace(spaceInfo)) {
      yield put(getHomeRemoveFavoriteSucceededAction(action.item.id, action.item.contentType));
      showUndoRemoveFavoriteToast(action.item, false, spaceInfo);
    } else {
      yield put(contentFavoriteRemoved(action.item));
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.favoritesError()));
  }
}

export function removeFavorite(item: IFavoritableItemInfo): Promise<IResult> {
  if(FeatureFlags.crassFavorites && item.contentType as any === 'metric') {
    const favorite: FavoriteRepresentation = {
      contentId: item.id,
      contentType: item.contentType,
    };

    const userId = SessionUserSelectors.sessionUserId(getAppStore().getState());
    return FavoritesResource.remove(favorite, userId);
  }
  else {
    const idForFavoriting = item.idForFavoriting ? item.idForFavoriting : item.id;
    return Promise.resolve(Favorites.remove(idForFavoriting, item.contentType));
  }

}

const maxCharactersForName = 24;

function showUndoRemoveFavoriteToast(item: IFavoritableItemInfo, updateContentCounts: boolean, spaceInfo: SpaceInfo) {
  showActionableToast({
    testId: 'undo-favorite',
    message: Messages.favoriteRemoved({ NAME: L10n.elideIfLong(item.name, maxCharactersForName) }),
    actionMessage: Messages.favoriteRemovedUndo(),
    action:  () => {
      getAppStore().dispatch(addContentFavoriteAction(item));
      if (updateContentCounts) {
        getAppStore().dispatch(incrementContentCount(item.id, spaceInfo.spaceType, item.contentType));
      }
    }
  });
}

function getFavoriteContentType(contentType: string): FavoriteItemType {
  if (!contentTypeCanBeFavorite(contentType)) {
    throw Error(`Items of type "${contentType}" cannot be a favorite.`);
  }
  return contentType;
}

export function* handleCallReorderItem(action: CallReorderItemAction) {
  try {
    const assetListItems: readonly AssetListItem[] = yield select(selectAssetListItems);
    const contentItems: readonly IExplorableItemTypeInfo[] = yield select(selectContentFromAppState);
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);

    const order: readonly IOrderClause[] = yield select(selectContentSort);
    if (order.length !== 0 && order[0].field !== OrderKeys.position) {
      if (FeatureFlags.confirmReorderFavorites) {
        const result: ConfirmDragDropOrderDialog.ConfirmOrCancel = yield call(ConfirmDragDropOrderDialog.open);
        if (result === 'cancel') {
          if (action.fromIndex < action.indexToMoveAfter) {
            yield put(reorderItem(action.indexToMoveAfter + 1, action.fromIndex - 1, spaceInfo));
          } else {
            yield put(reorderItem(action.indexToMoveAfter + 1, action.fromIndex, spaceInfo));
          }
          return;
        }
      }

      const favoriteOrdering = generateNewFavoriteOrder(contentItems, assetListItems);
      yield call(reorderFavorites, favoriteOrdering);

      _.defer(() => {
        // Because of the slow favorites SOLR core we can't just reload the page
        // with the default sort because they'll show things in the incorrect (old) order
        // Instead we have to update the url and have FavoriteSpace *not* fetch new items.
        // And we have to update the store directly because the url change won't trigger it.
        getAppStore().dispatch(setIgnoreNextOrderUrlChange());
        getAppStore().dispatch(setOrderAction(getFavoritesSpaceDefaultSortOrder()));
        getAppStore().dispatch(resetContentBusyFetching());
        getAppStore().dispatch(selectSortOrder(getFavoritesSpaceDefaultSortOrder()));
      });
      return;
    }

    const sourceItem = contentItems[action.indexToMoveAfter + 1];
    const favoriteId = getCorrespondingFavoriteId(sourceItem, assetListItems);
    const favoriteIdToMoveAfter = getFavoriteIdToMoveAfter(action, contentItems, assetListItems);
    const result = yield call(reorderFavorite, favoriteId, favoriteIdToMoveAfter);

    if (!_.isEmpty(result) && result.errors && result.errors.length > 0) {
      yield put(requestFailed(result.errors[0], Messages.favoritesReorderError()));
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.favoritesError()));
  }
}

export function generateNewFavoriteOrder(
  contentItems: readonly IExplorableItemTypeInfo[],
  assetListItems: readonly AssetListItem[],
): FavoriteOrdering[] {
  const favoriteOrdering: FavoriteOrdering[] = [];
  let favoriteIdToMoveAfter = '-1';
  for (let i = 0; i < contentItems.length; i++) {
    const favoriteId = getCorrespondingFavoriteId(contentItems[i], assetListItems);
    favoriteOrdering.push({ favoriteId, favoriteIdToMoveAfter });
    favoriteIdToMoveAfter = favoriteId;
  }
  return favoriteOrdering;
}

export function reorderFavorite(favoriteId: string, favoriteIdToMoveAfter: string) {
  return Favorites.orderFavorites([{
    favoriteId: favoriteId,
    favoriteIdToMoveAfter: favoriteIdToMoveAfter,
  }]);
}

export function reorderFavorites(order: FavoriteOrdering[]) {
  return Favorites.orderFavorites(order);
}

function getFavoriteIdToMoveAfter(
  action: ReorderItemAction,
  contentItems: readonly IExplorableItemTypeInfo[],
  assetListItems: readonly AssetListItem[],
): string {
  // If moving to the beginning, the orderFavorites API requires that the favoriteIdToMoveAfter should just be -1
  if (action.indexToMoveAfter < 0) {
    return '-1';
  }
  return getCorrespondingFavoriteId(contentItems[action.indexToMoveAfter], assetListItems);
}

function getCorrespondingFavoriteId(
  content: IExplorableItemTypeInfo,
  assetListItems: readonly AssetListItem[],
): string {
  const datasourceMatchesAssetListItem = (item: AssetListItem) => {
    if (FeatureFlags.useCollectionsFavorites) {
      return (item as IFavorite).objectIdForFavoriting === (content as IDataSourceInfo).idForFavoriting && item.objectType === content.contentType;
    }
    return (item as IFavorite).objectIdForFavoriting === content.id && item.objectType === content.contentType;
  };

  const contentMatchesAssetListItem = (item: AssetListItem) => {
    // The content type uses toLowerCase because data roles are stored as 'datarole' in the asset list, while the FE
    // content type is known as 'dataRole'
    return item.objectId === content.id && item.objectType.toLowerCase() === content.contentType.toLowerCase();
  };

  const item = content.contentType === 'datasource'
    ? assetListItems.find(datasourceMatchesAssetListItem)
    : assetListItems.find(contentMatchesAssetListItem);

  return item.id;
}
